import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    InputSignal,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { LoginContainerComponent } from '@connect-services/client/components/login-container/login-container.component';
import { PasswordRulesVerificationComponent } from '@connect-services/client/components/password-rules-verification/password-rules-verification.component';
import { VersionComponent } from '@connect-services/client/components/version/version.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AbstractSecurityComponent } from '@connect-services/client/pages/security/abstract-security/abstract-security.component';
import { CapitalizePipe } from '@connect-services/client/pipes/capitalize.pipe';
import { PasswordRulesService } from '@connect-services/client/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { ConfirmedPasswordFormGroup, SecurityFormField } from '@shared/client/models/form.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CapitalizePipe,
        LoadingComponent,
        LoginContainerComponent,
        PasswordRulesVerificationComponent,
        RessourcesModule,
        VersionComponent,
    ],
    selector: 'app-change-password',
    styleUrl: '../abstract-security/abstract-security.component.scss',
    templateUrl: '../abstract-security/abstract-security.component.html',
})
export class ChangePasswordComponent extends AbstractSecurityComponent implements OnInit {
    public override actionLabel = 'confirmNewPassword';
    public override fieldNames = [SecurityFormField.PASSWORD, SecurityFormField.CONFIRM_PASSWORD];
    public override readonly formGroup: WritableSignal<ConfirmedPasswordFormGroup> = signal<ConfirmedPasswordFormGroup>(
        {} as ConfirmedPasswordFormGroup,
    );
    public override title = 'enterNewPassword';
    public readonly token: InputSignal<string> = input.required<string>();

    private readonly passwordRulesService = inject(PasswordRulesService);
    private readonly router = inject(Router);

    protected override initForm(): ConfirmedPasswordFormGroup {
        return this.securityService.getConfirmedPasswordForm(this.organizationPasswordRules);
    }

    protected override async preInit(): Promise<void> {
        if (!this.securityService.isTokenValid(this.token())) {
            await this.router.navigate(['/', RoutePathEnum.LOGIN]);
            throw new Error('Invalid token');
        }

        try {
            const user = await this.securityService.getMe(this.token());
            this.organizationPasswordRules = await firstValueFrom(
                this.passwordRulesService.passwordRulesControllerGetOne({
                    authorization: `Bearer ${this.token()}`,
                    id: user.organization.id,
                }),
            );
        } catch {
            this.organizationPasswordRules = null;
        }
    }

    protected override async submitAction(): Promise<void> {
        const formControls = this.formGroup().controls;
        await this.securityService.changePassword(formControls[SecurityFormField.PASSWORD].value, this.token());
    }
}
